import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import type { Apartment } from "../types";

/**
 * Pick an apartment for a lead on the chessboard instead of a long list.
 * Free flats can be chosen, booked and sold ones are shown for context only.
 * Starts on the rooms the lead asked for.
 */
export const ApartmentPickerDialog = ({
  open,
  onOpenChange,
  current,
  roomsWanted,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: Identifier | null | undefined;
  roomsWanted?: number | null;
  onPick: (id: Identifier | null) => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const isMobile = useIsMobile();
  const { data: apartments = [] } = useGetList<Apartment>(
    "apartments",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "number", order: "ASC" },
    },
    { enabled: open },
  );
  const sections = useMemo(
    () => [...new Set(apartments.map((a) => a.section))].sort((a, b) => a - b),
    [apartments],
  );
  const currentApartment = apartments.find((a) => a.id === current);
  const [section, setSection] = useState<number>();
  const [rooms, setRooms] = useState<number>(roomsWanted ?? 0);
  const shownSection = section ?? currentApartment?.section ?? sections[0];
  const inSection = apartments.filter((a) => a.section === shownSection);
  const floors = [...new Set(inSection.map((a) => a.floor))].sort(
    (a, b) => b - a,
  );
  const perFloor = Math.max(
    1,
    ...floors.map((f) => inSection.filter((a) => a.floor === f).length),
  );
  const roomOptions = [...new Set(apartments.map((a) => a.rooms))].sort();
  const price = (a: Apartment) =>
    a.total_area && a.price_per_m2
      ? Math.round(Number(a.total_area) * Number(a.price_per_m2))
      : null;
  const money = (value: number) =>
    value >= 1000
      ? `$${Math.round(value / 1000).toLocaleString(locale)}k`
      : `$${value}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[min(1100px,calc(100vw-1.5rem))] max-w-none overflow-y-auto sm:max-w-none">
        <DialogHeader>
          <DialogTitle>{translate("crm.apartments.pick_title")}</DialogTitle>
          <DialogDescription>
            {translate("crm.apartments.pick_hint")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {sections.length > 1
            ? sections.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={value === shownSection}
                  onClick={() => setSection(value)}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-sm font-semibold",
                    value === shownSection
                      ? "border-primary bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground",
                  )}
                >
                  {translate("crm.apartments.section", { section: value })}
                </button>
              ))
            : null}
          <span className="mx-1 h-5 w-px bg-border" />
          {[0, ...roomOptions].map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={rooms === value}
              onClick={() => setRooms(value)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm font-semibold",
                rooms === value
                  ? "border-[#2f7a50] bg-[#2f7a50] text-white"
                  : "bg-card text-muted-foreground",
              )}
            >
              {value === 0
                ? translate("crm.apartments.all")
                : translate("crm.apartments.rooms_short", { count: value })}
            </button>
          ))}
        </div>

        <div
          className="grid gap-1.5"
          style={{
            gridTemplateColumns: `${isMobile ? 18 : 36}px repeat(${perFloor}, minmax(0, 1fr))`,
          }}
        >
          {floors.map((floor) => [
            <div
              key={`floor-${floor}`}
              className="flex items-center justify-center text-xs font-bold text-muted-foreground"
            >
              {floor}
            </div>,
            ...inSection
              .filter((a) => a.floor === floor)
              .sort((a, b) => a.number - b.number)
              .map((apartment) => {
                const free = apartment.status === "free";
                const chosen = apartment.id === current;
                const fits = rooms === 0 || apartment.rooms === rooms;
                const total = price(apartment);
                return (
                  <button
                    key={apartment.id}
                    type="button"
                    disabled={!free && !chosen}
                    onClick={() => onPick(apartment.id)}
                    title={`${apartment.number} · ${apartment.rooms}к · ${apartment.total_area ?? "?"} м²`}
                    className={cn(
                      "flex flex-col items-center justify-center rounded-lg px-0.5 py-1.5 tabular-nums transition",
                      isMobile ? "aspect-square" : "min-h-16",
                      free
                        ? "bg-[#2f7a50] text-white hover:brightness-110"
                        : apartment.status === "booked"
                          ? "bg-[#e3ac45] text-[#3d2a00]"
                          : "bg-[#dcdfd6] text-[#9aa092] dark:bg-[#343a3b]",
                      !fits && "opacity-25",
                      chosen && "outline-3 outline-offset-2 outline-[#d89a2b]",
                    )}
                  >
                    <span className="text-sm font-bold leading-tight">
                      {apartment.number}
                    </span>
                    {isMobile ? null : (
                      <span className="text-[11px] leading-tight opacity-90">
                        {apartment.rooms}к · {apartment.total_area ?? "?"}
                      </span>
                    )}
                    {!isMobile && free && total ? (
                      <span className="text-[11px] font-semibold leading-tight">
                        {money(total)}
                      </span>
                    ) : null}
                  </button>
                );
              }),
          ])}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <i className="size-3 rounded-sm bg-[#2f7a50]" />
              {translate("crm.apartments.counts.free")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="size-3 rounded-sm bg-[#e3ac45]" />
              {translate("crm.apartments.counts.booked")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="size-3 rounded-sm bg-[#dcdfd6]" />
              {translate("crm.apartments.counts.sold")}
            </span>
          </div>
          {current != null ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onPick(null)}
            >
              {translate("crm.funnel.lead.no_apartment_choice")}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
